import type { IDistanceMeasureLinePropertyBagModel } from '$lib/models/IDistanceMeasureLinePropertyBagModel';
import type { IDrawnPolygonPart, IDrawnPolygonShape } from '$lib/models/IPolygonModel';
import type { IPolylinePropertyBagModel } from '$lib/models/IPolylinePropertyBagModel';
import {
	ArcType,
	CallbackProperty,
	Cartesian2,
	Cartesian3,
	Cartographic,
	Color,
	ConstantPositionProperty,
	ConstantProperty,
	CustomDataSource,
	CzmlDataSource,
	defined,
	Ellipsoid,
	Entity,
	HeadingPitchRoll,
	HeightReference,
	HorizontalOrigin,
	JulianDate,
	KeyboardEventModifier,
	NearFarScalar,
	PinBuilder,
	PointGraphics,
	PolygonGraphics,
	PolygonHierarchy,
	Math as CesiumMath,
	PolylineDashMaterialProperty,
	PolylineGraphics,
	PropertyBag,
	Rectangle,
	RequestErrorEvent,
	RuntimeError,
	sampleTerrainMostDetailed,
	Scene,
	SceneMode,
	ScreenSpaceEventHandler,
	ScreenSpaceEventType,
	Transforms,
	VerticalOrigin,
	Viewer,
	clone,
	ColorMaterialProperty,
	LabelGraphics,
	BoundingSphere,
	CallbackPositionProperty,
} from 'cesium';
import { HTTPError } from 'ky';
import { get } from 'svelte/store';
import { v4 as uuidv4 } from 'uuid';
import { PoleAndLineService } from '../../services/pole-and-line-service';
import { llToUtm } from '../../utils/coordinate-converter';
import { handleUnauthorizedResourceFetch, useDataSources } from './cesium-datasources';
import { selectedMapEntity, selectedMapEntityPropertyBag } from './cesium-entity-store';
import { OriginalCesiumInputActions } from './cesium-original-input-actions';
import { busyStateMessage, useCesiumViewer } from './cesium-viewer';
import { activeTool, mapToolsStore } from './map-tools-store';
import {
	cartesianToLatitudeLongitude,
	createPoint,
	createTerrainProfilePolylineBetweenEntities,
	doesPolygonContainDuplicateEdges,
	EntityTypes,
	generateCzmlDocument,
	getEntityPropertyBag,
	getPolygonBoundariesAsLatLngs,
	getPolygonByPolygonReferenceId,
	getPolygonEdgesSortedByDistanceToPoint,
	getPolygonSelectableEdgePointsByPosition,
	getPolylineDistanceInMeters,
	setSelectedEntity,
	toggleEntityVisuals,
	redrawOperationalMarkingsIfEnabled,
	handleContextualizedCzmlResponse,
    entityIsPartOfMastpoint
} from './map-utils';
import { mapModeStore, type IMapModeStore } from './map-mode-store';
import type { IMastPointMinimalModel } from '$lib/models/IMastPointMinimalModel';
import { toast } from '$lib/components/organisms/toast/toast';
import { ENTITY_ID_PREFIX } from '$lib/utils/constants';
import { confirmModal } from '$lib/components/organisms/confirmModal/confirmModal';
import { collectGroupEntities, getGroupRoot } from './maptools/utils';

type StartEndPositionEvent = {
	startPosition: Cartesian2;
	endPosition: Cartesian2;
};
//Keep Box rotation
let lastKnownRotation = 0;

const handleKeyPress = (e: KeyboardEvent) => {
	if(e.key === 'Escape' || e.key === 'Esc') {
		const toolToDeactivate = get(activeTool);
		if(toolToDeactivate){
			get(mapToolsStore)[toolToDeactivate].deactivateTool();
		}
	}
}

const toggleEscapeToolListener = ({toggleOn}: {toggleOn: boolean}) => {
	if(toggleOn){
		document.addEventListener('keydown', handleKeyPress, false);
	}else {
		document.removeEventListener('keydown', handleKeyPress, false);
	}

}

/**
 * Temporarily disable entity selection when certain drawing tools are enabled */
const disableDefaultLeftClickHandler = (screenSpaceEventHandler: ScreenSpaceEventHandler): void => {
	const viewer = useCesiumViewer();
	screenSpaceEventHandler.setInputAction(() => {
		viewer.selectedEntity = undefined;
	}, ScreenSpaceEventType.LEFT_CLICK);
};

/***
 * When triggered, Cesium will generate a Blob image based on the currently rendered map.
 * The image will include any active WMS/WMTS layers, but not the overlaying menu, infobox, Cesium buttons or similar artefacts.
 */
const captureCesiumScreenshot = () => {
    confirmModal(
        `Ønsker du å åpne en separat fane i nettleseren med et bilde av det aktive kartutsnittet? Bildet eksisterer kun i nettleseren, men kan etter fane-åpning lagres til enheten ved å høyreklikke og velge 'lagre'.`,
		() => {
			const viewer = useCesiumViewer();
			viewer.render();
			viewer.canvas.toBlob(
				(blob) => {
					if (blob) {
						const blobURL = URL.createObjectURL(blob);
						window.open(blobURL, '_blank');
					} else {
						console.error('No blob');
					}
				},
				'image/png',
				100
			);
		},
		() => {},
		{
			labels: {
				title: 'Bekreft',
				confirm: 'Ja',
				cancel: 'Nei'
			}
		}
    )
};

/**
 *
 * @param screenSpaceEventHandler - Cesium viewer's screen space event handler
 * @param move - Whether to allow moving polygon edges (`true`) or not (`false`)
 */
const toggleMovePolygonEdge = ({
	screenSpaceEventHandler,
	move
}: {
	screenSpaceEventHandler: ScreenSpaceEventHandler;
	move: boolean;
}): void => {
	const viewer = useCesiumViewer();
	removeAllScreenSpaceInputActions(viewer, screenSpaceEventHandler);

	mapToolsStore.disableAllToolsExceptToggleToolByName('createTerrainProfileFromPoles', viewer);

	if (move) {
		screenSpaceEventHandler.removeInputAction(
			ScreenSpaceEventType.MOUSE_MOVE,
			KeyboardEventModifier.SHIFT
		);

		// Find polygon belonging to edge
		if (viewer.selectedEntity) {
			const parentPolygonId = getEntityPropertyBag<IDrawnPolygonPart>(
				viewer.selectedEntity
			).parentPolygonId;
			const polygon = getPolygonByPolygonReferenceId({ polygonReferenceId: parentPolygonId });

			if (
				polygon &&
				defined(polygon) &&
				polygon.polygon &&
				polygon.polygon.hierarchy &&
				viewer.selectedEntity.position
			) {
				const selectedPolygonHierarchy: PolygonHierarchy = polygon.polygon.hierarchy.getValue(
					new JulianDate()
				);
				const selectedEdgePosition = viewer.selectedEntity.position.getValue(new JulianDate());

				if (selectedEdgePosition) {
					// Find the closest polygon edge to the selected point
					const sortedDistancesBetweenPolygonEdgesAndPoint = getPolygonEdgesSortedByDistanceToPoint(
						{
							polygon: selectedPolygonHierarchy,
							point: selectedEdgePosition
						}
					);

					screenSpaceEventHandler.setInputAction(
						(event: StartEndPositionEvent) => {
							if (viewer.selectedEntity && defined(viewer.selectedEntity)) {
								const newPosition = viewer.scene.pickPosition(event.endPosition);
								if (defined(newPosition) && polygon.polygon) {
									selectedPolygonHierarchy.positions[
										sortedDistancesBetweenPolygonEdgesAndPoint[0].index
									] = newPosition;

									// If the selected polygon edge is the first placed polygon, it will also be the last, and thus the last edge must also be updated (when sorted, this element is in place 1)
									if (
										sortedDistancesBetweenPolygonEdgesAndPoint[0].index === 0 &&
										sortedDistancesBetweenPolygonEdgesAndPoint[0].distance ===
											sortedDistancesBetweenPolygonEdgesAndPoint[1].distance
									) {
										selectedPolygonHierarchy.positions[
											sortedDistancesBetweenPolygonEdgesAndPoint[1].index
										] = newPosition;
									}

									polygon.polygon.hierarchy = new CallbackProperty(() => {
										return new PolygonHierarchy(selectedPolygonHierarchy.positions);
									}, false);

									viewer.selectedEntity.position = new ConstantPositionProperty(newPosition);
								}
							}
						},
						ScreenSpaceEventType.MOUSE_MOVE,
						KeyboardEventModifier.SHIFT
					);
				}
			}
		}
	} else {
		// If the user disables the tool, remove the input action event listener
		screenSpaceEventHandler.removeInputAction(
			ScreenSpaceEventType.MOUSE_MOVE,
			KeyboardEventModifier.SHIFT
		);
	}
};

/**
 * Tool for exporting terrain profiles to external programs
 * 
 * @param screenSpaceEventHandler - Cesium viewer's screen space event handler
 */
const toggleSelectPolesForTerrainProfileCreation = (): void => {
	const viewer = useCesiumViewer();
	const {scene, screenSpaceEventHandler} = viewer;
	removeAllScreenSpaceInputActions(viewer, screenSpaceEventHandler);
	const { terrainProfilePolylineDataSource, mapdataDataSource } = useDataSources();

	if (!viewer.dataSources.contains(terrainProfilePolylineDataSource)) {
		viewer.dataSources.add(terrainProfilePolylineDataSource);
	}

	mapToolsStore.disableAllToolsExceptToggleToolByName('createTerrainProfileFromPoles', viewer);
	if (get(mapToolsStore).createTerrainProfileFromPoles.toolEnabled) {
		toggleEscapeToolListener({toggleOn: true});
		viewer.scene.screenSpaceCameraController.enableTranslate = false;
		viewer.scene.screenSpaceCameraController.enableLook = false;

		screenSpaceEventHandler.setInputAction(function (click: { position: Cartesian2 }) {
			const pickedObject = scene.pick(click.position);

			if (defined(pickedObject)) {
				const entity: Entity = pickedObject.id;
				const entityDataSource = entity.entityCollection.owner;
				//const anyPreviouslySelectedEntities = get(mapToolsStore).selectedMapEntities.length;

				if (
					entityDataSource !== get(mapModeStore).activeDataSource &&
					entityDataSource !== mapdataDataSource
				) {
					toast({
						type: 'warning',
						title: 'Ugyldig valg',
						description: 'Du kan ikke velge mastepunkt fra inaktiv plan. Vennligst aktiver visning av planen som inneholder dette mastepunktet'
					});
					return;
				}

				if (entity.id.includes(ENTITY_ID_PREFIX.MASTPOINT) || entityIsPartOfMastpoint(getEntityPropertyBag(entity))) {
                    const mastpointEntity = getGroupRoot(entity)
                    const polesOnMastpoint = collectGroupEntities(mastpointEntity, mastpointEntity.entityCollection).filter((e) => e.id.startsWith(ENTITY_ID_PREFIX.POLE));
					const { data: entityData } = 
						getEntityPropertyBag<IMastPointMinimalModel.MastPointPropertyBagModel>(mastpointEntity);

					const selectedEntities = get(mapToolsStore).selectedMapEntities;

					// Check if the selected entity was already previously selected.
					const isEntityAlreadySelected =
						selectedEntities.filter((_entity: Entity) => {
							const { data: _entityData } =
								getEntityPropertyBag<IMastPointMinimalModel.MastPointPropertyBagModel>(_entity);

							if (entityData.id === _entityData.id) {
								return mastpointEntity;
							}
						}).length > 0;

					/* There is no support for this backend in the time of writing, but will be. Activate these
					** lines of code if specs change.
					**
					if (anyPreviouslySelectedEntities) {
						//No support cross datasources. Check if selected entity is part of the same datasource as previous entities
						const isEntityInSameDatasource =
							selectedEntities.filter((_entity: Entity) => {
								const prevSelectedDataSource = _entity.entityCollection.owner;

								if (entityDataSource === prevSelectedDataSource) {
									return entity;
								}
							}).length === selectedEntities.length;

						if (!isEntityInSameDatasource) {
							console.warn('Vennligst velg mastepunkt fra samme datasett.');
							return;
						}
					}*/

					if (isEntityAlreadySelected) {
						// If so, deselect the entity.
						mapToolsStore.deselectSpecificMapEntity(mastpointEntity);
                        if(polesOnMastpoint?.length){
                            toggleEntityVisuals(polesOnMastpoint, false);
                        }else{
						toggleEntityVisuals([mastpointEntity], false);
                        }
					} else {
						// Otherwise, add the selected entity to the array of selected entities and change the entity's color.
						mapToolsStore.setSelectedMapEntities([mastpointEntity], true);
                        if(polesOnMastpoint?.length){
                            toggleEntityVisuals(polesOnMastpoint, true);
                        }else{
						toggleEntityVisuals([mastpointEntity], true);
                        }
					}

					// Draw a ground polyline between the selected entities to display the path of the to-be-generated terrain profile.
					createTerrainProfilePolylineBetweenEntities({
						entities: get(mapToolsStore).selectedMapEntities,
						dataSource: terrainProfilePolylineDataSource,
						polylineWidth: 10
					});
				}
			}
		}, ScreenSpaceEventType.LEFT_CLICK);
	} else {
		toggleEscapeToolListener({toggleOn: false});
		viewer.scene.screenSpaceCameraController.enableTranslate = true;
		viewer.scene.screenSpaceCameraController.enableLook = true;

        const selectedEntities = get(mapToolsStore).selectedMapEntities;
        busyStateMessage.set('Deaktiverer terrengprofilverktøy...');

        const entitiesToToggle = selectedEntities.map((entity) => {
            const groupEntities = collectGroupEntities(entity, entity.entityCollection).filter((e) => e.id.startsWith(ENTITY_ID_PREFIX.POLE));
            if(groupEntities?.length){
                return [...groupEntities];
            } else return entity;
        }).flat();

		toggleEntityVisuals(entitiesToToggle, false);

		mapToolsStore.update((store) => {
			store.selectedMapEntities = [];
			return store;
		});

		// Clear the datasource for any potentially lingering terrain profile polyline when disabling the tool.
		terrainProfilePolylineDataSource.entities.removeAll();
		// If the user disables the tool, remove the input action event listener
		//screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
        busyStateMessage.set('');
	}
};

/**
 * Draws the polygon from various settings set in store. Typically triggered by deactivation of polygon drawing tool
 * @returns The polygon entity
 */
const drawPolygonShape = () => {
	const {polygonColor, polygonShapePoints, polygonEntity, polygonDatasource} = get(mapToolsStore).drawPolygon;

	if(!polygonEntity || !polygonDatasource) {
		toast({
			type: 'error',
			title: 'Kan ikke oppdatere polygon',
			description: 'En uventet feil oppstod. Mangler informasjon om polygon. Vennligst prøv igjen.'
		});
		return polygonEntity;
	};

	polygonEntity.polygon = 
		new PolygonGraphics({
			hierarchy: new CallbackProperty(
				() => new PolygonHierarchy(polygonShapePoints),
				false
			),
			heightReference: HeightReference.CLAMP_TO_GROUND,
			material: polygonColor || Color.WHITE.withAlpha(0.7)
		})
	if(!polygonDatasource.entities.getById(polygonEntity.id)){
		polygonDatasource.entities.add(polygonEntity);
	}
	
	return polygonEntity;

};

const terminatePolygonShape = async (
	mode: 'DRAWING_ONLY' | 'PROJECT_CREATION' | 'PROJECT_EDITING' | 'PLAN_CREATION' | 'PLAN_EDITING'
) => {
	
	const viewer = useCesiumViewer();
	const tempDatasourceName = 'polygonCreationTempDatasource';
	const tempDatasourcesToRemove = viewer.dataSources.getByName(tempDatasourceName);
	if(tempDatasourcesToRemove.length){
		tempDatasourcesToRemove.forEach((datasource) => viewer.dataSources.remove(datasource));
	}
	/**
	 * Right click terminates the polygon which means the last shape point should be excluded
	 */
	//activeShapePoints.pop();
	mapToolsStore.update((store) => {
		const activeShapePoints = store.drawPolygon.polygonShapePoints;
		store.drawPolygon.polygonShapePoints = [...activeShapePoints, activeShapePoints[0]];
		return store;
	});
	const drawnPolygon = drawPolygonShape();

	// A timeout is needed to not trigger the context menu of the browser on termination.
	await new Promise((r) => setTimeout(r, 150));

	if (mode === 'PROJECT_CREATION') {
		mapModeStore.update((store) => {
			store.newProjectPolygonEntity = drawnPolygon;
			store.expandableProjectPickerCurrentView = 'CREATE_PROJECT';
			return store;
		});
	}

	if (mode === 'PROJECT_EDITING') {
		mapModeStore.update((store) => {
			store.newProjectPolygonEntity = drawnPolygon;
			store.expandableProjectPickerCurrentView = 'EDIT_PROJECT_DETAILS';
			return store;
		});
	}

	if (mode === 'PLAN_CREATION') {
		mapModeStore.update((store) => {
			store.newPlanPolygonEntity = drawnPolygon;
			store.expandableProjectPickerCurrentView = 'CREATE_PLAN_DETAILS';
			return store;
		});
	}

	if (mode === 'PLAN_EDITING') {
		mapModeStore.update((store) => {
			store.newPlanPolygonEntity = drawnPolygon;
			store.expandableProjectPickerCurrentView = 'EDIT_PLAN_DETAILS';
			return store;
		});
	}
	
	const {polygonEntity, polygonDatasource} = get(mapToolsStore).drawPolygon;

	if(!polygonDatasource || !polygonEntity){
		toast({
			type: 'error',
			title: 'Kan ikke terminere polygon',
			description: 'En uventet feil oppstod. Mangler informasjon om polygon. Vennligst prøv igjen.'
		});
		return;
	}
		//floatingPoint && sandbox_polygonDataSource.entities.remove(floatingPoint);
		//polygonDatasource.entities.remove(activeShape as Entity);
	
	removeSelectablePointsFromPolygonEdges({ referencePolygonId: polygonEntity.id, datasource: polygonDatasource });

	mapToolsStore.update((store) => {
		store.drawPolygon.polygonShapePoints = [];
		store.drawPolygon.polygonEntity = null;
		return store;
	})
	//Reset polygon properties
	//floatingPoint = undefined;
	//activeEdgePoints = [];
	//activeShapePoints = [];

	//referencePolygonId = `sandbox_polygon:${uuidv4()}`;
	if (mode !== 'DRAWING_ONLY') {
		mapToolsStore.disableAllTools(viewer);
		viewer.scene.screenSpaceCameraController.enableTranslate = true;
		viewer.scene.screenSpaceCameraController.enableLook = true;
	}
};

const createProjectAreaReferencePolygon = (projectData: IMapModeStore) => {
	if(!projectData || !projectData.latestFetchedProjectData) return;
	const viewer = useCesiumViewer();
	const tempDatasourceName = 'polygonCreationTempDatasource';
	const tempDatasource = new CzmlDataSource(tempDatasourceName);
	const cartesianPositions = Cartesian3.fromDegreesArray(projectData.latestFetchedProjectData.area.coordinates[0].flat(1));
	const projectPolygonEntity = new Entity({
		id: uuidv4(),
		polygon: new PolygonGraphics({
			hierarchy: new PolygonHierarchy(cartesianPositions),
			heightReference: HeightReference.CLAMP_TO_GROUND,
			material: new ColorMaterialProperty(Color.BLUEVIOLET.withAlpha(0.4))
		})
	});
	const labelForProjectPolygon = new Entity({
		id: uuidv4(),
		label: new LabelGraphics({
			backgroundColor: Color.BLACK,
			showBackground: true,
			verticalOrigin: VerticalOrigin.BOTTOM,
			horizontalOrigin: HorizontalOrigin.CENTER,
			backgroundPadding: new Cartesian2(10, 6),
			scaleByDistance: new NearFarScalar(0.0, 1.0, 8.0e6, 0.0),
			font: '500 14px Poppins',
			text: 'Prosjektområde',
			// I don't know how I came up with this magic number, but it works! (So far)
			disableDepthTestDistance: 1.2742018*10**7,
			heightReference: HeightReference.RELATIVE_TO_GROUND
		}),
		position: new ConstantPositionProperty(BoundingSphere.fromPoints(cartesianPositions).center)

	})
	tempDatasource.entities.add(projectPolygonEntity);
	tempDatasource.entities.add(labelForProjectPolygon);
	viewer.dataSources.add(tempDatasource);
}

/**
 * Activates the tool for drawing polygon. 
 * 
 * @param sandbox_polygonDataSource - The datasource where the polygon should be added
 * @param mode - The drawing context
 * @returns void
 */
const activatePolygonDrawingTool = (
	sandbox_polygonDataSource: CustomDataSource,
	mode: 'DRAWING_ONLY' | 'PROJECT_CREATION' | 'PROJECT_EDITING' | 'PLAN_CREATION' | 'PLAN_EDITING'
): void => {
	const {toolEnabled} = get(mapToolsStore).drawPolygon;
	// If tool already is enabled do nothing
	if (toolEnabled) {
		return;
	}
	const viewer = useCesiumViewer();
	const screenSpaceEventHandler = viewer.screenSpaceEventHandler
	const scene = viewer.scene;
	const projectData = get(mapModeStore);
	let polygonId = `sandbox_polygon:${uuidv4()}`;
	
	toggleEscapeToolListener({
		toggleOn: true
	});
	// Reset input actions - May not be needed
	// removeAllScreenSpaceInputActions(viewer, screenSpaceEventHandler);
	
	// Polygons may be created in different datasources, even datasources which should be created when activating this tool.
	if (!viewer.dataSources.contains(sandbox_polygonDataSource)) {
		viewer.dataSources.add(sandbox_polygonDataSource);
	}

	mapToolsStore.update((store) => {
		store.drawPolygon.polygonDrawingMode = mode;
		store.drawPolygon.polygonDatasource = sandbox_polygonDataSource;
		return store;
	})

	// Make sure no other tools than current tool is selected.
	mapToolsStore.disableAllToolsExceptToggleToolByName('drawPolygon', viewer);

	// Deselect any potentially selected entity at the time of enabling the polygon drawing tool.
	viewer.selectedEntity = undefined;

	// Create and visualize an additional polygon for reference in certain modes. 
	if(projectData.latestFetchedProjectData && ['PROJECT_EDITING', 'PLAN_CREATION', 'PLAN_EDITING'].includes(mode)){
		createProjectAreaReferencePolygon(projectData);
	}
	
	
	mapToolsStore.setCurrentPolygon(new Entity({
		id: polygonId,
		properties: {
			entityType: EntityTypes.POLYGON_SHAPE
		}
	}));
	
	disableDefaultLeftClickHandler(screenSpaceEventHandler);
	viewer.scene.screenSpaceCameraController.enableTranslate = false;
	viewer.scene.screenSpaceCameraController.enableLook = false;

	/*screenSpaceEventHandler.setInputAction((event: StartEndPositionEvent) => {
		if (floatingPoint && defined(floatingPoint)) {
			const newPosition = viewer.scene.pickPosition(event.endPosition);
			if (defined(newPosition)) {
				floatingPoint.position = new ConstantPositionProperty(newPosition);
				activeShapePoints.pop();
				activeShapePoints.push(newPosition);
			}
		}
	}, ScreenSpaceEventType.MOUSE_MOVE);*/

	screenSpaceEventHandler.setInputAction(async () => {
		const activeShapePoints = get(mapToolsStore).drawPolygon.polygonShapePoints;
		// Polygons require a minimum of three visual edges (minimum of four activePoints if floatingpoint exists)
		if (activeShapePoints.length >= 3) {
			
			// To mimic all other drawing tools behaviour, continue drawing after right click
			if(mode === 'DRAWING_ONLY') {
				await terminatePolygonShape(mode);
				// Instantiate new polygon
				polygonId = `sandbox_polygon:${uuidv4()}`
				mapToolsStore.setCurrentPolygon(new Entity({
					id: polygonId,
					properties: {
						entityType: EntityTypes.POLYGON_SHAPE
					}
				}));
			} else {
				deactivatePolygonDrawingTool(mode);
			}
		} else {
			alert('Et polygon må minst ha 3 punkter');
			deactivatePolygonDrawingTool(mode);
		}
	}, ScreenSpaceEventType.RIGHT_CLICK);

	// Enable adding line Entities using SHIFT+CLICK
	screenSpaceEventHandler.setInputAction((event: { position: Cartesian2 }) => {
		let earthPosition: Cartesian3 | undefined;

		if (scene.mode === SceneMode.SCENE3D) {
			earthPosition = viewer.scene.pickPosition(event.position); // Correct point when mousing over terrain
		} else if (scene.mode === SceneMode.SCENE2D) {
			earthPosition = viewer.camera.pickEllipsoid(event.position); // Good for 2D (non-terrain)
		} else {
			alert(
				'INFO: 2.5D mode is not good for placing elements on map. Use 2D or 3D. No element was placed.'
			);
			earthPosition = undefined;
		}
		// `earthPosition` will be undefined if our mouse is not over the globe.
		if (earthPosition && defined(earthPosition)) {
			const {polygonShapePoints, polygonEntity} = get(mapToolsStore).drawPolygon;
			mapToolsStore.update((store) => {
				store.drawPolygon.polygonShapePoints = [...store.drawPolygon.polygonShapePoints, earthPosition];
				return store;
			})
			if(!polygonEntity){
				toast({
					type: 'error',
					title: 'Kan ikke tegne polygon',
					description: 'En uventet feil oppstod. Mangler informasjon om polygon. Vennligst prøv igjen.'
				});
				return;
			}
			/*if (polygonShapePoints.length === 0) {
				floatingPoint = createPoint({
					position: earthPosition,
					referencePolygonId,
					sandbox_polygonDataSource,
					entityType: EntityTypes.POLYGON_PART
				});*/
				//floatingPoint = createPoint(earthPosition);
				//polygonShapePoints.push(earthPosition);
				/*const dynamicPositions = new CallbackProperty(
					() => new PolygonHierarchy(polygonShapePoints),
					false
				);

			}
			//createPoint(earthPosition);*/
			createPoint({
				position: earthPosition,
				referencePolygonId: polygonEntity.id,
				sandbox_polygonDataSource,
				entityType: EntityTypes.POLYGON_PART
			});
			drawPolygonShape();
		}
	}, ScreenSpaceEventType.LEFT_CLICK);
	
};

const cancelPolygonDrawing = (mode: 'DRAWING_ONLY' | 'PROJECT_CREATION' | 'PROJECT_EDITING' | 'PLAN_CREATION' | 'PLAN_EDITING', polygonEntity: Entity) => {
	const viewer = useCesiumViewer();
	const tempDatasourceName = 'polygonCreationTempDatasource';
	const tempDatasourcesToRemove = viewer.dataSources.getByName(tempDatasourceName);
	if(tempDatasourcesToRemove.length){
		tempDatasourcesToRemove.forEach((datasource) => viewer.dataSources.remove(datasource));
	}
	
	if (mode === 'PROJECT_CREATION') {
		mapModeStore.update((store) => {
			store.expandableProjectPickerCurrentView = 'CREATE_PROJECT';
			return store;
		});
	}

	if (mode === 'PROJECT_EDITING') {
		mapModeStore.update((store) => {
			store.expandableProjectPickerCurrentView = 'EDIT_PROJECT_DETAILS';
			return store;
		});
	}

	if (mode === 'PLAN_CREATION') {
		mapModeStore.update((store) => {
			store.expandableProjectPickerCurrentView = 'CREATE_PLAN_DETAILS';
			return store;
		});
	}

	if (mode === 'PLAN_EDITING') {
		mapModeStore.update((store) => {
			store.expandableProjectPickerCurrentView = 'EDIT_PLAN_DETAILS';
			return store;
		});
	}
	const polygonDatasource = polygonEntity?.entityCollection?.owner as CustomDataSource | undefined;
	if(!polygonDatasource) return;

	removeSelectablePointsFromPolygonEdges({ referencePolygonId: polygonEntity.id, datasource: polygonDatasource });
	polygonDatasource.entities.remove(polygonEntity);
	
	mapToolsStore.update((store) => {
		store.drawPolygon.polygonShapePoints = [];
		store.drawPolygon.polygonEntity = null;
		return store;
	})
	//Reset polygon properties
	//floatingPoint = undefined;
	//activeEdgePoints = [];
	//activeShapePoints = [];

	//referencePolygonId = `sandbox_polygon:${uuidv4()}`;
	if (mode !== 'DRAWING_ONLY') {
		mapToolsStore.disableAllTools(viewer);
		viewer.scene.screenSpaceCameraController.enableTranslate = true;
		viewer.scene.screenSpaceCameraController.enableLook = true;
	}
}


/**
 * Deactivate the tool for drawing polygon.
 * @param mode - The drawing context
 */
const deactivatePolygonDrawingTool = async (mode?: 'DRAWING_ONLY' | 'PROJECT_CREATION' | 'PROJECT_EDITING' | 'PLAN_CREATION' | 'PLAN_EDITING') => {
	const viewer = useCesiumViewer();
	const screenSpaceEventHandler = viewer.screenSpaceEventHandler;
	const {polygonEntity} = get(mapToolsStore).drawPolygon;
	toggleEscapeToolListener({
		toggleOn: false
	});
	if(mode === undefined){
		mode = get(mapToolsStore).drawPolygon.polygonDrawingMode ?? 'DRAWING_ONLY'
	}
	
	if(polygonEntity?.polygon?.hierarchy && polygonEntity.polygon.hierarchy.getValue(new JulianDate).positions.length >= 3){
		// Terminate polygon if an entity is drawn
		await terminatePolygonShape(mode);
	} else if(polygonEntity){
		cancelPolygonDrawing(mode, polygonEntity);
	}

	mapToolsStore.disableAllTools(viewer);

	// Reset input actions
	removeAllScreenSpaceInputActions(viewer, screenSpaceEventHandler);	
	viewer.scene.screenSpaceCameraController.enableTranslate = true;
	viewer.scene.screenSpaceCameraController.enableLook = true;
}

/**
 * Remove a specific user-drawn polygon (as opposed to "project polygons") by the polygon's ID
 * @param polygonId
 */
const removeDrawnPolygon = (polygonId: string | undefined) => {
	if (typeof polygonId === 'undefined') {
		return;
	}

	const { sandbox_polygonDataSource } = useDataSources();

	// Retrieve the Cesium entity of the drawn polygon
	let polygonEntity: Entity | undefined = sandbox_polygonDataSource.entities.getById(polygonId);

	if (!defined(polygonEntity)) {
		polygonEntity = sandbox_polygonDataSource.entities.values.find(
			(entity) => getEntityPropertyBag<IDrawnPolygonShape>(entity)?.referencePolygonId === polygonId
		);
	}

	if (defined(polygonEntity) && polygonEntity) {
		// Extract the reference polygon ID (which differs from the polygon's Entity ID), and is used for deleting the polygon's edge points
		const referencePolygonId =
			getEntityPropertyBag<IDrawnPolygonShape>(polygonEntity).referencePolygonId;

		// Don't raise unnecessary events
		sandbox_polygonDataSource.entities.suspendEvents();

		removeSelectablePointsFromPolygonEdges({ referencePolygonId, datasource: sandbox_polygonDataSource });

		// Finally remove the polygon (shape) and resume the entity event system
		const removed = sandbox_polygonDataSource.entities.removeById(polygonId);
		if (!removed) {
			sandbox_polygonDataSource.entities.removeById(polygonEntity.id);
		}
		sandbox_polygonDataSource.entities.resumeEvents();

		// Clicking on a polygon will automatically select all entities within its boundaries. Thus, upon removal of the polygon, deselect all selected entities.
		mapToolsStore.resetSelectedMapEntities();
	}
};

/**
 * Through passing the lineId of a measure distance line (2D measurement tool), remove all entities related to the line.
 */
const removeMeasureDistanceLine = ({ removeAll = false }: { removeAll?: boolean }) => {
	const { measureDistancePolylineDataSource } = useDataSources();

	if (removeAll) {
		measureDistancePolylineDataSource.entities.removeAll();
		return;
	}

	const selectedMeasureDistanceLineId = (
		get(selectedMapEntityPropertyBag) as IDistanceMeasureLinePropertyBagModel
	).lineId;

	const measureDistanceEntitiesToRemove = measureDistancePolylineDataSource.entities.values.filter(
		(entity) => {
			const entityPropertyBag: IDistanceMeasureLinePropertyBagModel = getEntityPropertyBag(entity);
			if (entityPropertyBag.lineId === selectedMeasureDistanceLineId) {
				return true;
			}

			return false;
		}
	);

	for (let i = 0; i < measureDistanceEntitiesToRemove.length; i++) {
		measureDistancePolylineDataSource.entities.remove(measureDistanceEntitiesToRemove[i]);
	}

	// measureDistancePolylineDataSource.entities.remove(entity);
};

/**
 * A specified polygon may have N edges with an associated point (entity) each.
 * This function removes both the point entity and the edge to which it is visually connected.
 */
const removePolygonEdgeAndPoint = (
	{ reselectPolygon }: { reselectPolygon: boolean } = { reselectPolygon: false }
): boolean => {
	const viewer = useCesiumViewer();
	const { sandbox_polygonDataSource } = useDataSources();

	if (viewer.selectedEntity) {
		// Find polygon belonging to edge
		const parentPolygonId = getEntityPropertyBag<IDrawnPolygonPart>(
			viewer.selectedEntity
		).parentPolygonId;
		const polygon = getPolygonByPolygonReferenceId({ polygonReferenceId: parentPolygonId });

		if (
			polygon &&
			defined(polygon) &&
			polygon.polygon &&
			polygon.polygon.hierarchy &&
			viewer.selectedEntity.position
		) {
			const selectedPolygonHierarchy: PolygonHierarchy = polygon.polygon.hierarchy.getValue(
				new JulianDate()
			);
			const selectedEdgePosition = viewer.selectedEntity.position.getValue(new JulianDate());

			if (!selectedEdgePosition) {
				alert("Could not retrieve the selected edge's position");
				return false;
			}

			if (selectedPolygonHierarchy.positions.length <= 3) {
				alert('A polygon requires a minimum of three edges.');
				return false;
			}

			// Find the closest polygon edge to the selected point
			const sortedDistancesBetweenPolygonEdgesAndPoint = getPolygonEdgesSortedByDistanceToPoint({
				polygon: selectedPolygonHierarchy,
				point: selectedEdgePosition
			});

			// If the polygon has four positions when attempting edge removal, check if two positions of them are the same (the initially drawn polygon point is duplicated). If there are four positions left and two are indeed duplicates, do not allow edge removal.
			if (
				selectedPolygonHierarchy.positions.length === 4 &&
				doesPolygonContainDuplicateEdges({ polygon: selectedPolygonHierarchy })
			) {
				alert('A polygon requires a minimum of three edges.');
				return false;
			}

			// Keep track of positions for all removed edges, as these positions will be used to remove any visually connected Points.
			const removedEdgePositions: Cartesian3[] = [];

			// Remove the edge from the polygon
			const removedMainEdge = selectedPolygonHierarchy.positions.splice(
				sortedDistancesBetweenPolygonEdgesAndPoint[0].index,
				1
			);
			removedEdgePositions.push(removedMainEdge[0]);

			// If the removed edge is the initial (start) drawn edge, also remove the final edge as that edge is programmatically added to complete the polygon ("full circle").
			if (
				sortedDistancesBetweenPolygonEdgesAndPoint[0].index === 0 &&
				sortedDistancesBetweenPolygonEdgesAndPoint[0].distance ===
					sortedDistancesBetweenPolygonEdgesAndPoint[1].distance
			) {
				const removedDuplicatePosition = selectedPolygonHierarchy.positions.splice(
					sortedDistancesBetweenPolygonEdgesAndPoint[1].index - 1,
					1
				);
				removedEdgePositions.push(removedDuplicatePosition[0]);
			}

			// In case one or more entities were not properly removed, we should log it.
			let entitiesWereRemoved = true;

			// Remove the point(s) (Cesium entity) from the now-removed edge of the polygon
			for (let i = 0; i < removedEdgePositions.length; i++) {
				// If there is a duplicate selectable edge point, remove that too
				const edgePointsEntities = getPolygonSelectableEdgePointsByPosition({
					position: removedEdgePositions[i]
				});

				for (let j = 0; j < edgePointsEntities.length; j++) {
					entitiesWereRemoved = sandbox_polygonDataSource.entities.remove(edgePointsEntities[j]);
				}
			}

			// Update the polygon's position hierarchy
			polygon.polygon.hierarchy = new CallbackProperty(() => {
				new PolygonHierarchy(selectedPolygonHierarchy.positions);
			}, false);

			if (entitiesWereRemoved) {
				if (reselectPolygon) {
					setSelectedEntity(polygon);
				} else {
					setSelectedEntity(undefined);
				}
				return true;
			} else {
				console.warn('Polygon edge was for some reason not removed');
				return false;
			}
		} else {
			console.error(`Polygon was not found`);
			return false;
		}
	} else {
		return false;
	}
};

/**
 * Adds N(=number of edges) number of Cesium points to a specified polygon's edges to allow editing/modifying the polygon's shape.
 * @param referencePolygonId - The reference ID (not necessarily the Entity ID) for the polygon onto which the edges/points should be connected.
 * @param polygonHierarchy - The polygon's hierarchy (position data structure) for the polygon.
 */
const addSelectablePointsToPolygonEdges = ({
	referencePolygonId,
	polygonHierarchy
}: {
	referencePolygonId: string;
	polygonHierarchy: PolygonHierarchy;
}) => {
	const { sandbox_polygonDataSource } = useDataSources();
	const viewer = useCesiumViewer();

	// The custom data source ("polygonDatasource") will automatically update the view when new entities are added
	if (!viewer.dataSources.contains(sandbox_polygonDataSource)) {
		viewer.dataSources.add(sandbox_polygonDataSource);
	}

	// Avoid raising Cesium events during polygon edge points creation.
	sandbox_polygonDataSource.entities.suspendEvents();
	// Do not render an edge for the last position as it is equal to the first
	for (let i = 0; i < polygonHierarchy.positions.length - 1; i++) {
		const position = polygonHierarchy.positions[i];
		createPoint({
			position,
			sandbox_polygonDataSource,
			referencePolygonId,
			entityType: EntityTypes.POLYGON_PART // TODO How to support both POLYGON_PART and PROJECT_POLYGON_PART
		});
	}
	sandbox_polygonDataSource.entities.resumeEvents();
};
/**
 * Removes all circular polygon parts (Handles) which marks edges on the polygon while drawing.
 * @param param0 
 */
const removeSelectablePointsFromPolygonEdges = ({
	referencePolygonId,
	datasource
}: {
	referencePolygonId: string;
	datasource: CustomDataSource
}) => {
	const viewer = useCesiumViewer();

	if (!viewer.dataSources.contains(datasource)) {
		viewer.dataSources.add(datasource);
	}
	const polygonPointsToDelete = datasource.entities.values.filter((entity) => {
		const entityPropertyBag = getEntityPropertyBag(entity);
		if (
			entityPropertyBag.entityType === EntityTypes.POLYGON_PART ||
			entityPropertyBag.entityType === EntityTypes.PROJECT_POLYGON_PART
		) {
			if ((entityPropertyBag as IDrawnPolygonPart).parentPolygonId === referencePolygonId) {
				return true;
			}
			return false;
		}
		return false;
	});

	// Remove all the polygon's edge points
	datasource.entities.suspendEvents();
	for (let i = 0; i < polygonPointsToDelete.length; i++) {
		datasource.entities.remove(polygonPointsToDelete[i]);
	}
	datasource.entities.resumeEvents();
};

const removeCornerPointsFromPolyline = ({ referenceLineId }: { referenceLineId: string }) => {
	const { sandbox_PolylineDataSource } = useDataSources();
	const viewer = useCesiumViewer();

	if (!viewer.dataSources.contains(sandbox_PolylineDataSource)) {
		viewer.dataSources.add(sandbox_PolylineDataSource);
	}

	//sandbox_PolylineDataSource.entities.suspendEvents();
	for (let i = 0; i < sandbox_PolylineDataSource.entities.values.length; i++) {
		const entity = sandbox_PolylineDataSource.entities.values[i];
		const entityPropertyBag = getEntityPropertyBag<IPolylinePropertyBagModel>(entity);
		if (
			entityPropertyBag.entityType === EntityTypes.SANDBOX_POLYLINE_PART &&
			entityPropertyBag.lineId === referenceLineId
		) {
			sandbox_PolylineDataSource.entities.remove(entity);
		}
	}
};

/*
 * Used together with the drawings removal tool in order to delete any potential lingering drag handles ("points") from the to-be-deleted (clicked) polyline.
 */
const removeAllPointsFromPolyline = ({ referenceLineId }: { referenceLineId: string }) => {
	const { sandbox_PolylineDataSource } = useDataSources();
	const viewer = useCesiumViewer();

	if (!viewer.dataSources.contains(sandbox_PolylineDataSource)) {
		viewer.dataSources.add(sandbox_PolylineDataSource);
	}

	const entitiesToDelete: Entity[] = sandbox_PolylineDataSource.entities.values.filter((entity) => {
		return (
			entity.id.startsWith('sandbox_polyline_drag_handle:') &&
			entity?.properties?.belongsToPolylineId?.getValue(new JulianDate()) === referenceLineId
		);
	});

	sandbox_PolylineDataSource.entities.suspendEvents();

	for (let i = 0; i < entitiesToDelete.length; i++) {
		sandbox_PolylineDataSource.entities.remove(entitiesToDelete[i]);
	}
	sandbox_PolylineDataSource.entities.resumeEvents();
};

const placeMastPoint = async (event: { position: Cartesian2 }, viewer: Viewer, scene: Scene) => {
	if (get(mapToolsStore)?.newMastPointsCurrentlyProcessing?.length >= 1) {
		toast({
			type: 'warning',
			title: 'Prosess opptatt',
			description: 'Maks ett mastepunkt kan opprettes samtidig. Vent til mastepunktet er ferdig opprettet, og prøv så igjen.'
		});
		return;
	}

	let earthPosition: Cartesian3 | undefined;
	if (scene.mode === SceneMode.SCENE3D) {
		//earthPosition = viewer.scene.pickPosition(event.position); // Correct point when mousing over terrain
		const ray = scene.camera.getPickRay(event.position);
		if (ray) {
			earthPosition = scene.globe.pick(ray, scene);
		}
	} else if (scene.mode === SceneMode.SCENE2D) {
		console.log('Scene2D');
		earthPosition = viewer.camera.pickEllipsoid(event.position); // Good for 2D (non-terrain)
	} else {
		alert(
			'INFO: 2.5D mode is not good for placing elements on map. Use 2D or 3D. No element was placed.'
		);
		earthPosition = undefined;
	}
	// `earthPosition` will be undefined if the mouse position is not within the globe boundaries.
	if (defined(earthPosition) && earthPosition) {
		busyStateMessage.set('Mastepunkt plasseres, vennligst vent.');

		const { latitude, longitude } = cartesianToLatitudeLongitude(earthPosition);

		const operationalMarking = uuidv4();

		const mastPointPositionUTM = llToUtm(latitude, longitude);

		mapToolsStore.update((store) => {
			store.newMastPointsCurrentlyProcessing = [
				operationalMarking,
				...store.newMastPointsCurrentlyProcessing
			];
			return store;
		});

        const templateId = get(mapToolsStore).addMastPoint.mastpointTemplate ?? null;

		const updatedPositions = await sampleTerrainMostDetailed(scene.terrainProvider, [
			Cartographic.fromCartesian(earthPosition)
		]);

		const newMastPoint = {
			operationalMarking,
			lat: latitude,
			lng: longitude,
			x1Coordinate: mastPointPositionUTM.northing,
			y1Coordinate: mastPointPositionUTM.easting,
			z1Coordinate: updatedPositions[0].height,
            constructionId: templateId
		};

		try {
			const mastPointCreationResponse = await PoleAndLineService.createMastPoint(newMastPoint);

			if (mastPointCreationResponse.status === 200) {
				const mastPointCZML = await mastPointCreationResponse.json();
				await handleContextualizedCzmlResponse(mastPointCZML)
				//Placing a mastpoint always returns infrastructure for a single datasource
			}

			mapToolsStore.update((store) => {
				store.newMastPointsCurrentlyProcessing = store.newMastPointsCurrentlyProcessing.filter(
					(newOperationalMarking) => newOperationalMarking !== operationalMarking
				);
				return store;
			});

			busyStateMessage.set('');
		} catch (error) {
			busyStateMessage.set('');
			// Clear the list of currently prosessing mastpoints to avoid lingering state on catch.
			mapToolsStore.update((store) => {
				store.newMastPointsCurrentlyProcessing = [];
				return store;
			});
			if (error instanceof RequestErrorEvent) {
				handleUnauthorizedResourceFetch(error);
			} else if (error instanceof HTTPError) {
				toast({
					type: 'error',
					title: 'Feil ved oppretting av mastepunkt',
					description: `En uventet feil oppstod i forbindelse med oppretting av mastepunkt. Feilmelding: ${error.message}`
				});
			} else {
				toast({
					type: 'error',
					title: 'Serverfeil',
					description: `En feil oppstod ved kontakt med server. Vennligst vent litt og prøv igjen.`
				})
				
			}
		}
	}
};

/**
 * Toggles if the user can add new mast points to the map by left clicking on the map.
 */
const toggleAddMastPointEntityToMap = async (
	scene: Scene,
	screenSpaceEventHandler: ScreenSpaceEventHandler
): Promise<void> => {
	const viewer = useCesiumViewer();
	removeAllScreenSpaceInputActions(viewer, screenSpaceEventHandler);

	mapToolsStore.disableAllToolsExceptToggleToolByName('addMastPoint', viewer);
	const { measureDistancePolylineDataSource } = useDataSources();
	let prevMastPointCoordinates: undefined | Cartesian3;
	let polylineEntity: Entity | undefined = undefined;
	let mastPointMeasurementLabel: Entity | undefined = undefined;

	if (get(mapToolsStore).addMastPoint.toolEnabled) {
		toggleEscapeToolListener({toggleOn: true});
		// Enable adding pole Entities using CLICK
		screenSpaceEventHandler.setInputAction((event: { position: Cartesian2 }) => {
			// Even though removeAllScreenSpaceInputActions is called, this event listener may still be registered.
			// We need to assume this can happen while we search for a more robust solution
			placeMastPoint(event, viewer, scene);

			const ray = scene.camera.getPickRay(event.position);
			if (ray) {
				prevMastPointCoordinates = scene.globe.pick(ray, scene);
			}
			if (polylineEntity) {
				measureDistancePolylineDataSource.entities.remove(polylineEntity);
				polylineEntity = undefined;
			}
			if (mastPointMeasurementLabel) {
				measureDistancePolylineDataSource.entities.remove(mastPointMeasurementLabel);
				mastPointMeasurementLabel = undefined;
			}
		}, ScreenSpaceEventType.LEFT_CLICK);

		//Adds a measurement polyline between placed mastPoints.
		screenSpaceEventHandler.setInputAction(async (event: { endPosition: Cartesian2 }) => {
			if (!prevMastPointCoordinates) return;
			let earthPosition: Cartesian3 | undefined;

			if (!defined(earthPosition)) {
				earthPosition = viewer.scene.pickPosition(event.endPosition);
			}

			//For some reason earthPosition is not Cartesian3 for every frame. May have to do with mouse cursor colliding with Entities. In case return void.
			if (!earthPosition) {
				return;
			}

			if (!polylineEntity) {
				polylineEntity = measureDistancePolylineDataSource.entities.add({
					id: `distance_polyline:mastpoint_measurement`,
					properties: new PropertyBag({
						entityType: EntityTypes.DISTANCE_MEASURE_LINE_PART
					}),
					polyline: new PolylineGraphics({
						positions: new CallbackProperty(() => [prevMastPointCoordinates, earthPosition], false),
						width: 3,
						arcType: ArcType.RHUMB,
						material: Color.YELLOW,
						clampToGround: true
					})
				});
			} else {
				polylineEntity.polyline!.positions = new CallbackProperty(
					() => [prevMastPointCoordinates, earthPosition],
					false
				);
			}
			if (!mastPointMeasurementLabel) {
				mastPointMeasurementLabel = measureDistancePolylineDataSource.entities.add({
					id: `distance_polyline_label:mastpoint_measurement`,
					properties: new PropertyBag({
						entityType: EntityTypes.DISTANCE_MEASURE_LINE_PART
					}),
					label: {
						show: true,
						showBackground: true,
						font: '14px monospace',
						eyeOffset: new Cartesian3(0.0, 0.0, -10.0),
						horizontalOrigin: HorizontalOrigin.CENTER,
						pixelOffset: new Cartesian2(0.0, -20.0),
						pixelOffsetScaleByDistance: new NearFarScalar(1.5e2, 3.0, 1.5e7, 0.5),
						disableDepthTestDistance: 1.2742018 * 10 ** 7
					}
				});
			} else {
				mastPointMeasurementLabel.position = new ConstantPositionProperty(earthPosition);
				const { totalDistanceInMeters } = await getPolylineDistanceInMeters(
					[prevMastPointCoordinates, earthPosition],
					viewer,
					true
				);
				if (mastPointMeasurementLabel && mastPointMeasurementLabel.label) {
					//const totalDistance = shouldClampToGround ? totalDistanceInMeters : totalDistanceIn3D;
					mastPointMeasurementLabel.label.text = new ConstantProperty(
						'Avstand: ' + totalDistanceInMeters.toFixed(2) + ' m'
					);
				}
			}
		}, ScreenSpaceEventType.MOUSE_MOVE);
	} else {
		toggleEscapeToolListener({toggleOn: false});
		polylineEntity = undefined;
		mastPointMeasurementLabel = undefined;
		redrawOperationalMarkingsIfEnabled();
	}
};

/**
 * Deactivates AddMastpointTool and reverts input actions and temporary datasources.
 */
const deactivateAddMastpointTool = () => {
	const viewer = useCesiumViewer();
	const screenSpaceEventHandler = viewer.screenSpaceEventHandler;
	const { measureDistancePolylineDataSource } = useDataSources();
	// If the user disables the "add pole" tool, remove the input action event listener
	//screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK, KeyboardEventModifier.SHIFT);
	const removalOfPolylineMeasurementEntity = measureDistancePolylineDataSource.entities.removeById("distance_polyline:mastpoint_measurement");
	const removalOfPolylineLabelMeasurementEntity = measureDistancePolylineDataSource.entities.removeById("distance_polyline_label:mastpoint_measurement");
	if(!removalOfPolylineMeasurementEntity || !removalOfPolylineLabelMeasurementEntity){
		console.warn("Ingen hjelpe-entiteter funnet etter deaktivering av mastepunkt-verktøy.");
	}
	toggleEscapeToolListener({toggleOn: false});
	// Also reset the LEFT_CLICK event handler (e.g., entity select)
	screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
	screenSpaceEventHandler.setInputAction(
		OriginalCesiumInputActions.LEFT_CLICK,
		ScreenSpaceEventType.LEFT_CLICK
	);
	mapToolsStore.update((store) => {
		store.addMastPoint.toolEnabled = false;
		return store;
	})
};

/**
 * Toggles if the user can add new labels (text) to the map by SHIFT+CLICK-ing on the map.
 */
const toggleAddLabelEntityToMap = async (): Promise<void> => {
	const viewer = useCesiumViewer();
	const {scene, screenSpaceEventHandler} = viewer;
	removeAllScreenSpaceInputActions(viewer, screenSpaceEventHandler);

	mapToolsStore.disableAllToolsExceptToggleToolByName('addLabel', viewer);

	if (get(mapToolsStore).addLabel.toolEnabled) {
		toggleEscapeToolListener({toggleOn: true});
		selectedMapEntity.set(undefined);

		viewer.scene.screenSpaceCameraController.enableTranslate = false;
		viewer.scene.screenSpaceCameraController.enableLook = false;

		// Enable adding pole Entities using CLICK
		screenSpaceEventHandler.setInputAction(async (event: { position: Cartesian2 }) => {
			let earthPosition: Cartesian3 | undefined;
			if (scene.mode === SceneMode.SCENE3D) {
				//earthPosition = viewer.scene.pickPosition(event.position); // Correct point when mousing over terrain
				const ray = scene.camera.getPickRay(event.position);
				if (ray) {
					earthPosition = scene.globe.pick(ray, scene);
				}
			} else if (scene.mode === SceneMode.SCENE2D) {
				console.log('Scene2D');
				earthPosition = viewer.camera.pickEllipsoid(event.position); // Good for 2D (non-terrain)
			} else {
				alert(
					'INFO: 2.5D mode is not good for placing elements on map. Use 2D or 3D. No element was placed.'
				);
				earthPosition = undefined;
			}
			// `earthPosition` will be undefined if the mouse position is not within the globe boundaries.
			if (defined(earthPosition) && earthPosition) {
				const { sandbox_LabelDataSource } = useDataSources();

				if (get(mapToolsStore).addLabel.labelText) {
					sandbox_LabelDataSource.entities.add({
						id: `sandbox_label:${uuidv4()}`,
						position: earthPosition,
						label: {
							text: get(mapToolsStore).addLabel.labelText,
							verticalOrigin: VerticalOrigin.BOTTOM,
							heightReference: HeightReference.CLAMP_TO_GROUND,
							/* eyeOffset: new Cartesian3(0.0, 0.0, -10.0),*/
							font: '20px sans-serif',
							fillColor: get(mapToolsStore).addLabel.labelTextColor,
							showBackground: true,
							disableDepthTestDistance: 1.2742018 * 10 ** 7
						}
					});
				}
			}
		}, ScreenSpaceEventType.LEFT_CLICK);
	} else {
		toggleEscapeToolListener({toggleOn: false});
		// If the user disables the "add label" tool, remove the input action event listener
		mapToolsStore.update(store => {
			store.addLabel.labelText = '';
			return store;
		});

		viewer.scene.screenSpaceCameraController.enableTranslate = true;
		viewer.scene.screenSpaceCameraController.enableLook = true;
		// Also reset the LEFT_CLICK event handler (e.g., entity select)
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
	}
};

/**
 * Trigger the toggle to either enable or disable new label creation. If enabled, attach relevant (mouse) event listeners to Cesium.
 */
const toggleRemoveEntities = () => {
	const viewer = useCesiumViewer();
	const {screenSpaceEventHandler} = viewer;

	removeAllScreenSpaceInputActions(viewer, screenSpaceEventHandler);
	mapToolsStore.disableAllToolsExceptToggleToolByName('removeSandboxEntities', viewer);
};

const toggleAddBoxEntityToMap = async (): Promise<void> => {
	const viewer = useCesiumViewer();
	const {scene, screenSpaceEventHandler} = viewer;
	const { sandbox_BoxDataSource } = useDataSources();
	removeAllScreenSpaceInputActions(viewer, screenSpaceEventHandler);
	mapToolsStore.disableAllToolsExceptToggleToolByName('insertNewBox', viewer);

	if (get(mapToolsStore).insertNewBox.toolEnabled) {
		toggleEscapeToolListener({toggleOn: true});
		selectedMapEntity.set(undefined);
		viewer.scene.screenSpaceCameraController.enableTranslate = false;
		viewer.scene.screenSpaceCameraController.enableLook = false;
		// Deselect any potentially selected entity at the time of enabling the box drawing tool
		viewer.selectedEntity = undefined;

		let anchorPosition: Cartesian3 | undefined = undefined;
		let activePosition: Cartesian3 | undefined = undefined;
		let lastKnownMapPosition: Cartesian3;
		let leftDownActive = false;

		/**
		 * Gets marker position and draws/updates specification of box.
		 *
		 * @PARAM positionData (Cartesian3) marker position
		 * @PARAM rotation (Number) marker movement in x-axis
		 * @PARAM mode (string) what should happen based on user input
		 * @RETURNS Entity (Specified box entity)
		 **/
		const preview = (
			positionData: Cartesian3 | undefined,
			rotation: number,
			mode: 'rotate' | 'move'
		) => {
			if (!positionData) return;

			const boxColor = get(mapToolsStore).insertNewBox.boxColor || Color.WHITE.withAlpha(0.7);
			const referenceBoxId = get(mapToolsStore).insertNewBox.uuid;

			if (!referenceBoxId) {
				mapToolsStore.setNewBoxEntity(uuidv4());

				return sandbox_BoxDataSource.entities.add({
					id: get(mapToolsStore).insertNewBox.uuid,
					position: positionData,
					orientation: new ConstantProperty(
						Transforms.headingPitchRollQuaternion(
							positionData,
							new HeadingPitchRoll(
								CesiumMath.toRadians(rotation ? rotation : 0),
								CesiumMath.toRadians(0.0),
								CesiumMath.toRadians(0.0)
							)
						)
					),
					box: {
						dimensions: get(mapToolsStore).insertNewBox.dimensions,
						material: boxColor
						//heightReference does not officially exist for box at the moment. Very unpredictable when used
						//heightReference: HeightReference.CLAMP_TO_GROUND
					}
				});
			} else {
				const entityInPreview: Entity | undefined =
					sandbox_BoxDataSource.entities.getById(referenceBoxId);

				if (!defined(entityInPreview)) {
					return;
				}
				//@ts-ignore - entityInPreview is defined
				entityInPreview.box.material = boxColor;
				//@ts-ignore - entityInPreview is defined
				entityInPreview.box.dimensions = get(mapToolsStore).insertNewBox.dimensions;

				if (mode === 'move') {
					/*------------SHOULD BE REVISED-------------------
					 **Since there is no CLAMP_TO_GROUND on a box entity, we need to calculate the z-dimension based on box height.
					 **As a default, the center position of the box is on the mouse pointers location, ie. the ground.
					 */
					positionData.z += get(mapToolsStore).insertNewBox.dimensions.z / 2;

					//Cesium CallbackProperty works by "accident" and should be replaced by CallbackPositionProperty if and when i gets released. See issue 5 below
					//https://github.com/CesiumGS/cesium/issues/8927
					//@ts-ignore - entityInPreview is defined
					entityInPreview.position = new CallbackPositionProperty(() => positionData, false);
				} else {
					entityInPreview.orientation = new ConstantProperty(
						Transforms.headingPitchRollQuaternion(
							positionData,
							new HeadingPitchRoll(
								CesiumMath.toRadians(rotation),
								CesiumMath.toRadians(0.0),
								CesiumMath.toRadians(0.0)
							)
						)
					);
				}
			}
		};

		// MOUSE_MOVE actually places an entity, but should also trigger a deletion unless it is placed with LEFT_CLICK
		// If left mouse is pressed, the entity should be rotated insted of moved around the map.
		screenSpaceEventHandler.setInputAction((event: StartEndPositionEvent) => {
			const newPosition = viewer.scene.pickPosition(event.endPosition);
			lastKnownMapPosition = newPosition;

			if (leftDownActive) {
				const movementToDegrees = (Math.abs(event.startPosition.x) / 3) % 360;
				const degrees = parseInt(movementToDegrees.toFixed(0));
				lastKnownRotation = degrees;
				preview(activePosition, degrees, 'rotate');
			} else {
				activePosition = newPosition;
				preview(newPosition, lastKnownRotation, 'move');
			}
		}, ScreenSpaceEventType.MOUSE_MOVE);

		// Enable adding box Entities using Left Click
		screenSpaceEventHandler.setInputAction((event: { position: Cartesian2 }) => {
			let earthPosition: Cartesian3 | undefined;
			if (scene.mode === SceneMode.SCENE3D) {
				earthPosition = viewer.scene.pickPosition(event.position);
			} else if (scene.mode === SceneMode.SCENE2D) {
				earthPosition = viewer.camera.pickEllipsoid(event.position);
			} else {
				alert(
					'INFO: 2.5D mode is not good for placing elements on map. Use 2D or 3D. No element was placed.'
				);
				earthPosition = undefined;
			}

			//Reset uuid when box is placed to create a new entity in preview
			mapToolsStore.setNewBoxEntity(undefined);

			if (!defined(anchorPosition)) {
				anchorPosition = earthPosition;
			}
		}, ScreenSpaceEventType.LEFT_CLICK);

		screenSpaceEventHandler.setInputAction(() => {
			viewer.scene.screenSpaceCameraController.enableRotate = false;
			leftDownActive = true;
		}, ScreenSpaceEventType.LEFT_DOWN);

		screenSpaceEventHandler.setInputAction(() => {
			viewer.scene.screenSpaceCameraController.enableRotate = true;
			leftDownActive = false;
		}, ScreenSpaceEventType.LEFT_UP);
	} else {
		toggleEscapeToolListener({toggleOn: false});
		viewer.scene.screenSpaceCameraController.enableTranslate = true;
		viewer.scene.screenSpaceCameraController.enableLook = true;
		viewer.scene.screenSpaceCameraController.enableRotate = true;
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_DOWN);
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_UP);
		// 
		screenSpaceEventHandler.setInputAction(
			OriginalCesiumInputActions.LEFT_CLICK,
			ScreenSpaceEventType.LEFT_CLICK
		);
		const boxStore = get(mapToolsStore).insertNewBox;
		const boxId = boxStore.uuid;
		const OMBox = boxStore.boxWhichNeedsOM;

		if(OMBox){
			sandbox_BoxDataSource.entities.removeById(OMBox);
			mapToolsStore.update(store => {
				store.insertNewBox.boxWhichNeedsOM = undefined;
				return store;
			})
		}
		//Actions performed when disabling box tool
		if (defined(boxId) && boxId) {
			//Delete current box, which is not manually placed by user
			sandbox_BoxDataSource.entities.removeById(boxId);
			//Reset boxEntity
			mapToolsStore.setNewBoxEntity(undefined);
		}
	}
};


const toggleEntityTypesToMap = async (): Promise<void> => {
	const viewer = useCesiumViewer();
	const {scene, screenSpaceEventHandler} = viewer;
	const { sandbox_BoxDataSource } = useDataSources();
	removeAllScreenSpaceInputActions(viewer, screenSpaceEventHandler);
	mapToolsStore.disableAllToolsExceptToggleToolByName('addEntityTypes', viewer);
	disableDefaultLeftClickHandler(screenSpaceEventHandler);

	if (get(mapToolsStore).addEntityTypes.toolEnabled) {
		selectedMapEntity.set(undefined);
		viewer.scene.screenSpaceCameraController.enableTranslate = false;
		viewer.scene.screenSpaceCameraController.enableLook = false;
		// Deselect any potentially selected entity at the time of enabling the box drawing tool
		viewer.selectedEntity = undefined;

		let anchorPosition: Cartesian3 | undefined = undefined;
		let activePosition: Cartesian3 | undefined = undefined;
		let lastKnownMapPosition: Cartesian3;
		let leftDownActive = false;

		/**
		 * Gets marker position and draws/updates specification of box.
		 *
		 * @PARAM positionData (Cartesian3) marker position
		 * @PARAM rotation (Number) marker movement in x-axis
		 * @PARAM mode (string) what should happen based on user input
		 * @RETURNS Entity (Specified box entity)
		 **/
		const preview = (
			positionData: Cartesian3 | undefined,
			rotation: number,
			mode: 'rotate' | 'move'
		) => {
			if (!positionData) return;
			const {uuid, type} = get(mapToolsStore).addEntityTypes;
			const staticCableBoothDimensions = new ConstantProperty(new Cartesian3(0.235, 0.7, 1.375));
			const staticNetStationDimensions = new ConstantProperty(new Cartesian3(2.23, 2.84, 2.23));
			if (!uuid) {
				mapToolsStore.setNewEntityType(uuidv4());

				return sandbox_BoxDataSource.entities.add(new Entity({
					id: get(mapToolsStore).addEntityTypes.uuid,
					position: positionData,
					orientation: new ConstantProperty(
						Transforms.headingPitchRollQuaternion(
							positionData,
							new HeadingPitchRoll(
								CesiumMath.toRadians(rotation ? rotation : 0),
								CesiumMath.toRadians(0.0),
								CesiumMath.toRadians(0.0)
							)
						)
					),
					point: new PointGraphics({
						color: Color.RED,
						heightReference: HeightReference.CLAMP_TO_GROUND
					}),
					model: {
						uri: `models/${type}.glb`,
						heightReference: HeightReference.CLAMP_TO_GROUND,
					}
				}));
			} else {
				const entityInPreview: Entity | undefined =
					sandbox_BoxDataSource.entities.getById(uuid);

				if (!defined(entityInPreview)) {
					return;
				}
				
				if (mode === 'move') {
					/*------------SHOULD BE REVISED-------------------
					 **Since there is no CLAMP_TO_GROUND on a box entity, we need to calculate the z-dimension based on box height.
					 **As a default, the center position of the box is on the mouse pointers location, ie. the ground.
					 */
					if(positionData?.z){
						//positionData.z += type === 'kabelskap' ? (staticCableBoothDimensions.getValue(new JulianDate).z - 0.7) : (staticNetStationDimensions.getValue(new JulianDate).z / 2);
					}
					//Cesium CallbackProperty works by "accident" and should be replaced by CallbackPositionProperty if and when i gets released. See issue 5 below
					//https://github.com/CesiumGS/cesium/issues/8927
					//@ts-ignore - entityInPreview is defined
					entityInPreview.position = new CallbackProperty(() => {
						return positionData;
					}, false);
				} else {
					entityInPreview.orientation = new ConstantProperty(
						Transforms.headingPitchRollQuaternion(
							positionData,
							new HeadingPitchRoll(
								CesiumMath.toRadians(rotation),
								CesiumMath.toRadians(0.0),
								CesiumMath.toRadians(0.0)
							)
						)
					);
				}
			}
		};

		// MOUSE_MOVE actually places an entity, but should also trigger a deletion unless it is placed with LEFT_CLICK
		// If left mouse is pressed, the entity should be rotated insted of moved around the map.
		screenSpaceEventHandler.setInputAction( async (event: StartEndPositionEvent) => {
			
			const newPosition = viewer.scene.pickPosition(event.endPosition);
			lastKnownMapPosition = newPosition;

			if (leftDownActive && newPosition) {
				const movementToDegrees = (Math.abs(newPosition.x) / 3) % 360;
				const degrees = parseInt(movementToDegrees.toFixed(0));
				lastKnownRotation = degrees;
				preview(activePosition, degrees, 'rotate');
			} else {
				activePosition = newPosition;
				preview(newPosition, lastKnownRotation, 'move');
			}
		}, ScreenSpaceEventType.MOUSE_MOVE);

		// Enable adding box Entities using Left Click
		screenSpaceEventHandler.setInputAction((event: { position: Cartesian2 }) => {
			let earthPosition: Cartesian3 | undefined;
			if (scene.mode === SceneMode.SCENE3D) {
				earthPosition = viewer.scene.pickPosition(event.position);
			} else if (scene.mode === SceneMode.SCENE2D) {
				earthPosition = viewer.camera.pickEllipsoid(event.position);
			} else {
				alert(
					'INFO: 2.5D mode is not good for placing elements on map. Use 2D or 3D. No element was placed.'
				);
				earthPosition = undefined;
			}

			//Reset uuid when box is placed to create a new entity in preview
			mapToolsStore.setNewEntityType(undefined);

			if (!defined(anchorPosition)) {
				anchorPosition = earthPosition;
			}
		}, ScreenSpaceEventType.LEFT_CLICK);

		screenSpaceEventHandler.setInputAction(() => {
			viewer.scene.screenSpaceCameraController.enableRotate = false;
			leftDownActive = true;
		}, ScreenSpaceEventType.LEFT_DOWN);

		screenSpaceEventHandler.setInputAction(() => {
			viewer.scene.screenSpaceCameraController.enableRotate = true;
			leftDownActive = false;
		}, ScreenSpaceEventType.LEFT_UP);
	} else {
		viewer.scene.screenSpaceCameraController.enableTranslate = true;
		viewer.scene.screenSpaceCameraController.enableLook = true;
		viewer.scene.screenSpaceCameraController.enableRotate = true;
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_DOWN);
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_UP);

		//Actions performed when disabling box tool
		const {uuid} = get(mapToolsStore).addEntityTypes;
		if (defined(uuid)) {
			//Delete current box, which is not manually placed by user
			sandbox_BoxDataSource.entities.removeById(uuid);
			//Reset boxEntity
			mapToolsStore.setNewBoxEntity(undefined);
		}
	}
};


/**
 * Toggles if the user can add new markers (billboards) to the map by CLICK-ing on the map.
 */
const toggleAddMarkerEntityToMap = async (): Promise<void> => {
	const viewer = useCesiumViewer();
	const { scene, screenSpaceEventHandler } = viewer;
	removeAllScreenSpaceInputActions(viewer, screenSpaceEventHandler);

	const pinBuilder = new PinBuilder();

	mapToolsStore.disableAllToolsExceptToggleToolByName('addMarker', viewer);

	if (get(mapToolsStore).addMarker.toolEnabled) {
		toggleEscapeToolListener({toggleOn: true});
		selectedMapEntity.set(undefined);

		viewer.scene.screenSpaceCameraController.enableTranslate = false;
		viewer.scene.screenSpaceCameraController.enableLook = false;

		// Enable adding marker Entities using CLICK
		screenSpaceEventHandler.setInputAction(async (event: { position: Cartesian2 }) => {
			let earthPosition: Cartesian3 | undefined;
			if (scene.mode === SceneMode.SCENE3D) {
				//earthPosition = viewer.scene.pickPosition(event.position); // Correct point when mousing over terrain
				const ray = scene.camera.getPickRay(event.position);
				if (ray) {
					earthPosition = scene.globe.pick(ray, scene);
				}
			} else if (scene.mode === SceneMode.SCENE2D) {
				console.log('Scene2D');
				earthPosition = viewer.camera.pickEllipsoid(event.position); // Good for 2D (non-terrain)
			} else {
				alert(
					'INFO: 2.5D mode is not good for placing elements on map. Use 2D or 3D. No element was placed.'
				);
				earthPosition = undefined;
			}
			// `earthPosition` will be undefined if the mouse position is not within the globe boundaries.
			if (defined(earthPosition) && earthPosition) {
				const { sandbox_MarkerDataSource } = useDataSources();

				sandbox_MarkerDataSource.entities.add({
					id: `sandbox_marker:${uuidv4()}`,
					position: earthPosition,
					billboard: {
						image: pinBuilder.fromColor(get(mapToolsStore).addMarker.markerColor, 48).toDataURL(),
						/* image: pinBuilder
							.fromText(`Kort tekst ny linje?`, Color.ROYALBLUE, 250)
							.toDataURL(), */
						verticalOrigin: VerticalOrigin.BOTTOM,
						heightReference: HeightReference.CLAMP_TO_GROUND
					}
				});
			}
		}, ScreenSpaceEventType.LEFT_CLICK);
	} else {
		toggleEscapeToolListener({toggleOn: false});
		// If the user disables the "add marker" tool, remove the input action event listener
		viewer.scene.screenSpaceCameraController.enableTranslate = true;
		viewer.scene.screenSpaceCameraController.enableLook = true;

		// Also reset the LEFT_CLICK event handler (e.g., entity select)
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
	}
};

/**
 * Toggles if the user can add new rectangles on the map
 */
const toggleDrawRectangleOnMap = async (): Promise<void> => {
	const viewer = useCesiumViewer();
	const { screenSpaceEventHandler } = viewer;
	removeAllScreenSpaceInputActions(viewer, screenSpaceEventHandler);

	mapToolsStore.disableAllToolsExceptToggleToolByName('drawRectangle', viewer);

	let selector: Entity;

	let rectangleSelector = new Rectangle();
	let cartesian = new Cartesian3();
	let tempCartographic = new Cartographic();
	let firstPoint = new Cartographic();
	let firstPointSet = false;
	let mouseDown = false;

	const { sandbox_RectangleDataSource } = useDataSources();

	if (get(mapToolsStore).drawRectangle.toolEnabled) {
		toggleEscapeToolListener({toggleOn: true});
		const getSelectorLocation = new CallbackProperty(function getSelectorLocation(time, result) {
			return Rectangle.clone(rectangleSelector, result);
		}, false);

		viewer.scene.screenSpaceCameraController.enableTranslate = false;
		viewer.scene.screenSpaceCameraController.enableLook = false;

		screenSpaceEventHandler.setInputAction(function startRectangleDrawing() {
			mouseDown = true;
			selector = sandbox_RectangleDataSource.entities.add({
				id: `sandbox_rectangle:${uuidv4()}`,
				show: false,
				rectangle: {
					coordinates: getSelectorLocation,
					material: get(mapToolsStore).drawRectangle.rectangleColor
				}
			});

			if (selector && selector.rectangle) {
				selector.rectangle.coordinates = getSelectorLocation;
			}
		}, ScreenSpaceEventType.LEFT_CLICK);

		screenSpaceEventHandler.setInputAction(function endRectangleDrawing() {
			if (selector?.rectangle?.coordinates) {
				selector.rectangle.coordinates = new ConstantProperty(rectangleSelector);

				sandbox_RectangleDataSource.entities.add({
					id: `sandbox_rectangle:${uuidv4()}`,
					show: true,
					rectangle: {
						coordinates: rectangleSelector,
						material: get(mapToolsStore).drawRectangle.rectangleColor
					}
				});

				sandbox_RectangleDataSource.entities.removeById(selector.id);

				selector = new Entity();
				rectangleSelector = new Rectangle();
				cartesian = new Cartesian3();
				tempCartographic = new Cartographic();
				firstPoint = new Cartographic();
				firstPointSet = false;
				mouseDown = false;
			}
		}, ScreenSpaceEventType.RIGHT_CLICK);

		screenSpaceEventHandler.setInputAction(function drawSelector(movement: {
			endPosition: Cartesian2;
		}) {
			if (!mouseDown) {
				return;
			}

			cartesian = viewer.scene.pickPosition(movement.endPosition);

			if (cartesian) {
				//mouse cartographic
				tempCartographic = Cartographic.fromCartesian(cartesian, Ellipsoid.WGS84, tempCartographic);

				if (!firstPointSet) {
					Cartographic.clone(tempCartographic, firstPoint);
					firstPointSet = true;
				} else {
					rectangleSelector.east = Math.max(tempCartographic.longitude, firstPoint.longitude);
					rectangleSelector.west = Math.min(tempCartographic.longitude, firstPoint.longitude);
					rectangleSelector.north = Math.max(tempCartographic.latitude, firstPoint.latitude);
					rectangleSelector.south = Math.min(tempCartographic.latitude, firstPoint.latitude);

					selector.show = true;
				}
			}
		},
		ScreenSpaceEventType.MOUSE_MOVE);
	} else {
		
		toggleEscapeToolListener({toggleOn: false});
		viewer.scene.screenSpaceCameraController.enableTranslate = true;
		viewer.scene.screenSpaceCameraController.enableLook = true;

		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);

		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);

		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);

		// Also reset the LEFT_CLICK event handler (e.g., entity select)
		/* screenSpaceEventHandler.setInputAction(
			OriginalCesiumInputActions.LEFT_CLICK,
			ScreenSpaceEventType.LEFT_CLICK,
			KeyboardEventModifier.SHIFT
		); */
	}
};

const removeAllDragHandlesExceptActiveHandleAndPolyline = (
	activeDragHandle: Entity,
	editableEntity: Entity
) => {
	const { sandbox_PolylineDataSource } = useDataSources();

	// Clone the initial entities
	const __activeDragHandle = clone(activeDragHandle);
	const __editableEntity = clone(editableEntity);

	let entitiesToDelete: Entity[] = [];

	entitiesToDelete = sandbox_PolylineDataSource.entities.values.filter((entity) => {
		return (
			entity.id.startsWith('sandbox_polyline_drag_handle:') &&
			entity?.properties?.belongsToPolylineId?.getValue(new JulianDate()) === editableEntity.id
		);
	});

	sandbox_PolylineDataSource.entities.suspendEvents();

	for (let i = 0; i < entitiesToDelete.length; i++) {
		sandbox_PolylineDataSource.entities.remove(entitiesToDelete[i]);
	}

	sandbox_PolylineDataSource.entities.remove(activeDragHandle);
	sandbox_PolylineDataSource.entities.remove(editableEntity);

	sandbox_PolylineDataSource.entities.resumeEvents();

	const _activeDragHandle = sandbox_PolylineDataSource.entities.add(__activeDragHandle);
	const _editableEntity = sandbox_PolylineDataSource.entities.add(__editableEntity);

	return {
		_activeDragHandle,
		_editableEntity
	};
};

/**
	 * Trigger the toggle to either enable or disable editing of drawn entities. If enabled, attach relevant (mouse) event listeners to Cesium.
	 */
const toggleEditDrawnEntity = () => {
	const viewer = useCesiumViewer();
	const { scene, screenSpaceEventHandler } = viewer;
	// If the tool is enabled, disable it and ensure removal of all entities related to the editing.
	const toolStore = get(mapToolsStore);
	if (toolStore.editDrawnEntity.toolEnabled) {
		toolStore.editDrawnEntity.toolEnabled = false;
		toolStore.editDrawnEntity.entityId = '';
		toggleEditPolylineInMap();
		return;
	}

	removeAllScreenSpaceInputActions(viewer, screenSpaceEventHandler);
	mapToolsStore.disableAllToolsExceptToggleToolByName('editDrawnEntity', viewer);
};

const generateEditablePolyline = (editableEntity: Entity | undefined, time: JulianDate) => {
	if (!editableEntity) return [] as Entity[];

	const { sandbox_PolylineDataSource } = useDataSources();

	const controlPoints: Entity[] = [];
	removeAllDrawnPolylineDragHandlesForEditablePolyline(editableEntity?.id);

	for (let i = 0; i < editableEntity?.polyline?.positions?.getValue(time).length; i++) {
		const currentPosition: Cartesian3 = editableEntity?.polyline?.positions?.getValue(time).at(i);

		const polylineHandle = sandbox_PolylineDataSource.entities.add({
			id: `sandbox_polyline_drag_handle:${uuidv4()}`,
			position: currentPosition,
			properties: {
				belongsToPolylineId: editableEntity.id,
				hasBeenMoved: false,
				isMiddlePoint: false
			},
			billboard: {
				disableDepthTestDistance: Number.POSITIVE_INFINITY,
				heightReference: HeightReference.CLAMP_TO_GROUND,
				verticalOrigin: VerticalOrigin.CENTER,
				image: `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA8AAAAPCAYAAAA71pVKAAAAAXNSR0IArs4c6QAAATtJREFUOE+lkz1PAkEQhp+7gsJobCCW/hCN9cVrAMFCDImYKBTwZyDxzkCMihR6HxaGSOvHH7EEC43GBAoxc8dGRMQzTre78+y8M/uuxteYBywgC8TGjgbABVACXtS+NpaQAhxjHT1fgJU1iMfhsQd3N3ByBJ0270AG8IVTcBpwqxbs7k9oGVvWbagUg40NwBN4AXiqWuizQHVH3YJKKVCwKHDTMMl5Vz9XnDxJmUELZwL3Wy6xpAiPGL4DuSwDgYcPXYgnIpJArwvLS+HAhm/D6KDKnNNG8H8q91sOsaQMP2JcurCVCXs+NUy2/zLttAnXbZoCiyWfazZ6Ye/30g0bysXPdxZCrOnVbJh1QeMQyqED5WH979420fM7sCreToTevr+F40Ygdaq3lV5p4QDYnPKrzke/6lUlfwAMtVpeEX0+AgAAAABJRU5ErkJggg==`
			}
		});

		controlPoints.push(polylineHandle);

		const nextPosition: Cartesian3 = editableEntity?.polyline?.positions?.getValue(time).at(i + 1);

		if (nextPosition) {
			const previousHandle = polylineHandle?.position?.getValue(time);

			if (!previousHandle) continue;

			const middlePoint = sandbox_PolylineDataSource.entities.add({
				id: `sandbox_polyline_drag_handle:${uuidv4()}`,
				properties: {
					belongsToPolylineId: editableEntity.id,
					hasBeenMoved: false,
					isMiddlePoint: true
				},
				position: Cartesian3.midpoint(previousHandle, nextPosition, new Cartesian3()),
				billboard: {
					disableDepthTestDistance: Number.POSITIVE_INFINITY,
					heightReference: HeightReference.CLAMP_TO_GROUND,
					verticalOrigin: VerticalOrigin.CENTER,
					image: `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA8AAAAPCAYAAAA71pVKAAAAAXNSR0IArs4c6QAAAUhJREFUOE9jZEAFbAwMDB4MDAyaDAwMzEhSfxkYGK4zMDDsYGBg+AUTZ0RSoM7AwBAUEKAp7uGtLqutIynExPSX7d9f5l+XLz99t2P77cebNlx/ycDAsI6BgeEmSB9MM0hjcHePh66ljZw8mmvg3ONHHj0sLdlxmYGBYS3IAJBmkFOLu3s89PBphJlw9Mijh+UlOy4xMDD0gjT7BQRqOpWU25rhshFdvKvj8KlNG67vA2kunzE7wExHV0ySWM2XLr54npW+6RRIc9Xh45mujIx/2YnV/P8f809bq+m7wZqPnEjzIVYjTJ2Lw6ItFNtMsp+vXH71PCN1A9jPFIU2OJ57+jz1LKxkcSYQmF+hCQUezyBxcArr6fPUxWfA8WOPHpYWoaYwmKGQtB2oKe7hBUnboOj7/5/557Urz99t3XoTZ9qGGUBSrgIAu+OJLAWdc8gAAAAASUVORK5CYII=`
				}
			});

			controlPoints.push(middlePoint);
		}
	}

	return controlPoints;
};

/**
 * Toggles if the user can edit existing polylines in the map.
 */
const toggleEditPolylineInMap = async (): Promise<void> => {
	const viewer = useCesiumViewer();
	const { sandbox_PolylineDataSource, sandbox_BoxDataSource } = useDataSources();
	const screenSpaceEventHandler = viewer.screenSpaceEventHandler;
	removeAllScreenSpaceInputActions(viewer, screenSpaceEventHandler);

	const time = new JulianDate();

	let editableEntity = sandbox_PolylineDataSource.entities.getById(
		get(mapToolsStore).editDrawnEntity.entityId
	);

	if (get(mapToolsStore).editDrawnEntity.toolEnabled) {
		if (!editableEntity) {
			console.log(`No editable entity found. Returning early.`);
			return;
		}

		if (!editableEntity.id.startsWith('sandbox_polyline')) {
			console.log(
				`Sandbox polyline is not the editable entity for some reason. See 'editableEntity' below. Returning early.`
			);
			console.log(editableEntity);
			return;
		}

		if (!editableEntity?.polyline?.positions) {
			console.log(`EditableEntity (polyline) has no positions.`);
			return;
		}

		// Handle left down for grabbing points
		let activePoint: Entity | null = null;
		let controlPoints: Entity[] = generateEditablePolyline(editableEntity, time) || [];

		viewer.screenSpaceEventHandler.setInputAction((event: { position: Cartesian2 }) => {
			if (!editableEntity?.polyline) return;

			const pick = viewer.scene.pick(event.position);

			if (!pick || !pick.id) return;

			const pickedObject = pick.id as Entity;

			if (
				pickedObject.id.startsWith('sandbox_polyline:') &&
				editableEntity?.id !== pickedObject.id
			) {
				// User attempts to select a different polyline for editing.
				// TODO: The code below is not sufficient for switching between editable polylines, and will cause a multitude of issues.
				// Until further, we simply do not allow the user to pick/select a different polyline, and the user must rather disable and re-enable the editing tool.

				/* removeAllDrawnPolylineDragHandlesForEditablePolyline(editableEntity.id);

				mapToolsStore.update((store) => {
					store.editDrawnEntity.entityId = pickedObject.id;
					store.editDrawnEntity.controlPoints = [];
					store.editDrawnEntity.middlePoints = [];

					return store;
				});

				editableEntity = sandbox_PolylineDataSource.entities.getById(pickedObject.id);

				controlPoints = generateEditablePolyline(editableEntity, time) || []; */

				viewer.scene.screenSpaceCameraController.enableRotate = false;
				return;
			}

			const pickedObjectIndex = controlPoints.findIndex(
				(point) =>
					point.id === pickedObject.id &&
					point?.properties?.belongsToPolylineId?.getValue(new JulianDate()) === editableEntity?.id
			);

			if (pickedObjectIndex !== -1 && controlPoints.at(pickedObjectIndex)) {
				controlPoints.at(pickedObjectIndex)?.properties?.hasBeenMoved.setValue(true);
				controlPoints.at(pickedObjectIndex)?.properties?.isMiddlePoint.setValue(false);

				const { _activeDragHandle, _editableEntity } =
					removeAllDragHandlesExceptActiveHandleAndPolyline(
						controlPoints.at(pickedObjectIndex) as Entity,
						editableEntity
					);

				activePoint = _activeDragHandle;
				editableEntity = _editableEntity;
			}

			viewer.scene.screenSpaceCameraController.enableRotate = false;
		}, ScreenSpaceEventType.LEFT_DOWN);

		// Handle left up for releasing points
		viewer.screenSpaceEventHandler.setInputAction(() => {
			// Renegerate entire line
			removeAllDrawnPolylineDragHandlesForEditablePolyline(editableEntity?.id);
			controlPoints = generateEditablePolyline(editableEntity, time) || [];

			viewer.scene.screenSpaceCameraController.enableRotate = true;
			activePoint = null;
		}, ScreenSpaceEventType.LEFT_UP);

		// Handle mouse move for updating point positions
		viewer.screenSpaceEventHandler.setInputAction(async (event: { endPosition: Cartesian2 }) => {
			if (activePoint) {
				const ray = viewer.camera.getPickRay(event.endPosition);
				if (!ray) return;

				const newPosition = viewer.scene.globe.pick(ray, viewer.scene);

				if (defined(newPosition) && newPosition) {
					const index = controlPoints.findIndex((point) => point.id === activePoint?.id);

					if (index === -1 && !controlPoints.at(index)) return;
					//@ts-ignore. Invalid TS-error
					controlPoints.at(index).position = new ConstantPositionProperty(newPosition);
					activePoint.position = new ConstantPositionProperty(newPosition);

					const dynamicPositions = new CallbackProperty(
						() =>
							controlPoints
								.filter((point) => !point.properties?.isMiddlePoint.getValue(time)) // Only use active polyline handles
								.map((point) => {
									return point.position instanceof Cartesian3
										? point.position
										: point.position?.getValue(time);
								}),
						false
					);

					if (!editableEntity?.polyline) return;
					editableEntity.polyline.positions = dynamicPositions;
				}
			}
		}, ScreenSpaceEventType.MOUSE_MOVE);
	} else {
		editableEntity = undefined;

		// Also reset the LEFT_CLICK event handler (e.g., entity select)
		screenSpaceEventHandler.setInputAction(
			OriginalCesiumInputActions.LEFT_CLICK,
			ScreenSpaceEventType.LEFT_CLICK
		);

		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_DOWN);
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_UP);
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);

		removeAllDrawnPolylineDragHandlesForEditablePolyline();
	}
};

/**
 * A polyline which is being edited will have drag handles ("points"/billboards) placed along it.
 * When the user wants to stop the polyline from being edited, those handles/points/billboards must be deleted.
 */
const removeAllDrawnPolylineDragHandlesForEditablePolyline = (belongsToPolylineId?: string) => {
	const { sandbox_PolylineDataSource } = useDataSources();

	let polylineDragHandlesToDelete: Entity[] = [];

	if (belongsToPolylineId) {
		polylineDragHandlesToDelete = sandbox_PolylineDataSource.entities.values.filter((entity) => {
			return (
				entity.id.startsWith('sandbox_polyline_drag_handle:') &&
				entity?.properties?.belongsToPolylineId?.getValue(new JulianDate()) === belongsToPolylineId
			);
		});
	} else {
		polylineDragHandlesToDelete = sandbox_PolylineDataSource.entities.values.filter((entity) => {
			return entity.id.startsWith('sandbox_polyline_drag_handle:');
		});
	}

	//sandbox_PolylineDataSource.entities.remove(points[0]);
	sandbox_PolylineDataSource.entities.suspendEvents();
	for (let i = 0; i < polylineDragHandlesToDelete.length; i++) {
		sandbox_PolylineDataSource.entities.remove(polylineDragHandlesToDelete[i]);
	}
	sandbox_PolylineDataSource.entities.resumeEvents();
};

/**
 * Resets each Cesium event handler to its original action.
 * @param viewer 
 * @param screenSpaceEventHandler 
 * 
 */
const removeAllScreenSpaceInputActions = (
	viewer: Viewer,
	screenSpaceEventHandler: ScreenSpaceEventHandler
) => {
	if (!screenSpaceEventHandler) return;

	screenSpaceEventHandler.removeInputAction(
		ScreenSpaceEventType.MOUSE_MOVE,
		KeyboardEventModifier.SHIFT
	);
	screenSpaceEventHandler.removeInputAction(
		ScreenSpaceEventType.LEFT_CLICK,
		KeyboardEventModifier.SHIFT
	);
	screenSpaceEventHandler.removeInputAction(
		ScreenSpaceEventType.RIGHT_CLICK,
		KeyboardEventModifier.SHIFT
	);
	screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
	screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
	screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
	screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
	screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_DOWN);
	screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_UP);

	screenSpaceEventHandler.setInputAction(
		OriginalCesiumInputActions.LEFT_CLICK,
		ScreenSpaceEventType.LEFT_CLICK
	);

	/* screenSpaceEventHandler.setInputAction(
		OriginalCesiumInputActions.LEFT_DOWN,
		ScreenSpaceEventType.LEFT_DOWN
	); */

	if (
		OriginalCesiumInputActions.LEFT_CLICK__SHIFT &&
		OriginalCesiumInputActions.RIGHT_CLICK__SHIFT &&
		OriginalCesiumInputActions.LEFT_CLICK
	) {
		screenSpaceEventHandler.setInputAction(
			OriginalCesiumInputActions.LEFT_CLICK__SHIFT,
			ScreenSpaceEventType.LEFT_CLICK,
			KeyboardEventModifier.SHIFT
		);
		screenSpaceEventHandler.setInputAction(
			OriginalCesiumInputActions.RIGHT_CLICK__SHIFT,
			ScreenSpaceEventType.RIGHT_CLICK,
			KeyboardEventModifier.SHIFT
		);

		// Also reset the LEFT_CLICK event handler (e.g., entity select)
		screenSpaceEventHandler.setInputAction(
			OriginalCesiumInputActions.LEFT_CLICK,
			ScreenSpaceEventType.LEFT_CLICK
		);
	}

	viewer.scene.screenSpaceCameraController.enableTranslate = true;
	viewer.scene.screenSpaceCameraController.enableLook = true;
};

/**
 * Used in conjuction with importing of previously exported CZML-based drawings.
 * Takes in a CZML (string) and creates map entities in the correct data sources for each entity type.
 */
const renderDrawingsFromCzml = async ({ czml, fileName }: { czml: any; fileName: string }) => {
	const czmlDataSource = new CzmlDataSource();
	try {
		await czmlDataSource.load(czml);

		const {
			sandbox_BoxDataSource,
			sandbox_LabelDataSource,
			sandbox_MarkerDataSource,
			sandbox_polygonDataSource,
			sandbox_PolylineDataSource,
			sandbox_RectangleDataSource,
			measureDistancePolylineDataSource,
			trenchEntityDatasource
		} = useDataSources();

		// Avoid raising events
		sandbox_BoxDataSource.entities.suspendEvents();
		sandbox_LabelDataSource.entities.suspendEvents();
		sandbox_MarkerDataSource.entities.suspendEvents();
		sandbox_polygonDataSource.entities.suspendEvents();
		sandbox_PolylineDataSource.entities.suspendEvents();
		sandbox_RectangleDataSource.entities.suspendEvents();
		measureDistancePolylineDataSource.entities.suspendEvents();

		czmlDataSource.entities.values.forEach((entity: Entity) => {
			if (entity.id.startsWith('sandbox_rectangle:')) {
				if (!sandbox_RectangleDataSource.entities.getById(entity.id)) {
					sandbox_RectangleDataSource.entities.add(entity);
				}
			} else if (entity.id.startsWith('distance_polyline_label:')) {
				if (!measureDistancePolylineDataSource.entities.getById(entity.id)) {
					measureDistancePolylineDataSource.entities.add(entity);
				}
			} else if (entity.id.startsWith('sandbox_polyline:')) {
				if (!sandbox_PolylineDataSource.entities.getById(entity.id)) {
					sandbox_PolylineDataSource.entities.add(entity);
				}
			}
			else if (entity.id.startsWith('cablePath:') || entity.id.startsWith('trenchPath:')) {
				if(!trenchEntityDatasource.entities.getById(entity.id)) {
					trenchEntityDatasource.entities.add(entity);
				}
			
			} else if (entity.id.startsWith('sandbox_polygon:')) {
				if (!sandbox_polygonDataSource.entities.getById(entity.id)) {
					sandbox_polygonDataSource.entities.add(entity);
				}
			} else if (entity.id.startsWith('distance_polyline:')) {
				if (!measureDistancePolylineDataSource.entities.getById(entity.id)) {
					measureDistancePolylineDataSource.entities.add(entity);
				}
			} else if (entity.id.startsWith('sandbox_label:')) {
				if (!sandbox_LabelDataSource.entities.getById(entity.id)) {
					sandbox_LabelDataSource.entities.add(entity);
				}
			} else if (entity.id.startsWith('sandbox_marker:')) {
				if (!sandbox_MarkerDataSource.entities.getById(entity.id)) {
					sandbox_MarkerDataSource.entities.add(entity);
				}
			} else if (entity.id.startsWith('sandbox_box:')) {
				if (!sandbox_BoxDataSource.entities.getById(entity.id)) {
					sandbox_BoxDataSource.entities.add(entity);
				}
			}
		});

		// Resume raising events
		sandbox_BoxDataSource.entities.resumeEvents();
		sandbox_LabelDataSource.entities.resumeEvents();
		sandbox_MarkerDataSource.entities.resumeEvents();
		sandbox_polygonDataSource.entities.resumeEvents();
		sandbox_PolylineDataSource.entities.resumeEvents();
		sandbox_RectangleDataSource.entities.resumeEvents();
		measureDistancePolylineDataSource.entities.resumeEvents();
	} catch (error) {
		if (error instanceof RuntimeError) {
			toast({
				type: 'warning',
				title: 'Ugyldig filformat',
				description: `Filen du lastet opp <em><code>(${fileName})</code></em> ser ut til å være ugyldig CZML.`
			});
		} else {
			console.log(error);
		}
	}
};

/*
 * If the user has drawn elements onto the map using the drawing tools, these drawings can be exported to CZML and saved onto the user's machine.
 */
const exportDrawingsToCzml = async () => {
	const {
		sandbox_LabelDataSource,
		sandbox_MarkerDataSource,
		sandbox_polygonDataSource,
		sandbox_PolylineDataSource,
		sandbox_RectangleDataSource,
		sandbox_BoxDataSource,
		measureDistancePolylineDataSource,
		trenchEntityDatasource
	} = useDataSources();

	if (!anyTemporaryDrawings()) {
		return [];
	}

	return generateCzmlDocument([
		sandbox_LabelDataSource.entities,
		sandbox_MarkerDataSource.entities,
		sandbox_polygonDataSource.entities,
		sandbox_PolylineDataSource.entities,
		sandbox_RectangleDataSource.entities,
		sandbox_BoxDataSource.entities,
		measureDistancePolylineDataSource.entities,
		trenchEntityDatasource.entities
	]);
};

export const anyTemporaryDrawings = () => {
	const {
		sandbox_LabelDataSource,
		sandbox_MarkerDataSource,
		sandbox_polygonDataSource,
		sandbox_PolylineDataSource,
		sandbox_RectangleDataSource,
		sandbox_BoxDataSource,
		measureDistancePolylineDataSource,
		trenchEntityDatasource
	} = useDataSources();

	if (
		sandbox_BoxDataSource.entities.values.length === 0 &&
		sandbox_LabelDataSource.entities.values.length === 0 &&
		sandbox_MarkerDataSource.entities.values.length === 0 &&
		sandbox_polygonDataSource.entities.values.length === 0 &&
		sandbox_PolylineDataSource.entities.values.length === 0 &&
		sandbox_RectangleDataSource.entities.values.length === 0 &&
		measureDistancePolylineDataSource.entities.values.length === 0 &&
		trenchEntityDatasource.entities.values.length === 0
	) {
		return false;
	} else {
		return true;
	}
}

/*
 * Removes all temporary drawings, whether drawn by the user during the session, or imported through the CZML drawings importer.
 * Returns the number of removed elements.
 */
const removeAllDrawings = (): number => {
	const {
		sandbox_LabelDataSource,
		sandbox_MarkerDataSource,
		sandbox_polygonDataSource,
		sandbox_PolylineDataSource,
		sandbox_RectangleDataSource,
		sandbox_BoxDataSource,
		measureDistancePolylineDataSource
	} = useDataSources();

	let numberOfEntitiesToRemove = 0;

	numberOfEntitiesToRemove += sandbox_LabelDataSource.entities.values.length;
	numberOfEntitiesToRemove += sandbox_MarkerDataSource.entities.values.length;
	numberOfEntitiesToRemove += sandbox_polygonDataSource.entities.values.length;
	numberOfEntitiesToRemove += sandbox_PolylineDataSource.entities.values.length;
	numberOfEntitiesToRemove += sandbox_RectangleDataSource.entities.values.length;
	numberOfEntitiesToRemove += measureDistancePolylineDataSource.entities.values.length;

	sandbox_LabelDataSource.entities.removeAll();
	sandbox_MarkerDataSource.entities.removeAll();
	sandbox_polygonDataSource.entities.removeAll();
	sandbox_PolylineDataSource.entities.removeAll();
	sandbox_RectangleDataSource.entities.removeAll();
	sandbox_BoxDataSource.entities.values.forEach(box => {
		// Remove only boxes not related to trench
		if(!box.properties?.data){
			numberOfEntitiesToRemove += 1;
			sandbox_BoxDataSource.entities.remove(box);
		}
	});
	measureDistancePolylineDataSource.entities.removeAll();

	return numberOfEntitiesToRemove;
};

/**
 * A collection of specific functions that disables maptools.
 *
 * Note:
 * Probably a better way to disable tools as we can pass these functions directly to
 * map-tools-store which handles deactivation of tools. Makes maintaining these snippets
 * a bit more managable as deactivation and activation is seperated and rather grouped
 * together with related functionality. May also fix a problem with ScreenSpaceEventHandler
 * which in some cases does not deactivate properly.
 *
 * TO DO: Add more deactivation of tools in separate functions.
 * @returns An array of functions related to deactivating mapTools.
 
const useToolDeactivation = () => {
	return {
		deactivateAddMastpointTool
	};
};
*/

export {
	terminatePolygonShape,
	toggleDrawRectangleOnMap,
	toggleMovePolygonEdge,
	toggleAddMastPointEntityToMap,
	toggleAddMarkerEntityToMap,
	toggleAddBoxEntityToMap,
	toggleSelectPolesForTerrainProfileCreation,
	toggleRemoveEntities,
	toggleEditDrawnEntity,
	activatePolygonDrawingTool,
	deactivatePolygonDrawingTool,
	deactivateAddMastpointTool,
	removeAllDrawnPolylineDragHandlesForEditablePolyline,
	toggleAddLabelEntityToMap,
	toggleEditPolylineInMap,
	removeDrawnPolygon,
	removePolygonEdgeAndPoint,
	removeSelectablePointsFromPolygonEdges,
	removeMeasureDistanceLine,
	removeCornerPointsFromPolyline,
	removeAllPointsFromPolyline,
	removeAllScreenSpaceInputActions,
	addSelectablePointsToPolygonEdges,
	captureCesiumScreenshot,
	renderDrawingsFromCzml,
	exportDrawingsToCzml,
	removeAllDrawings,
	toggleEntityTypesToMap,
	disableDefaultLeftClickHandler,
	toggleEscapeToolListener
};
